import _ from 'lodash';

import Locator from '../../../../shared/Locator';
import { useAddJobToNotificationQueue } from '../../../components/JobNotificationHub/Provider';
import { useAddToast } from '../../../contexts/ToastContext';
import { queryClient, useMutation } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { NewToast } from '../../../components/Toaster/types';
import type { Build } from '../../../types';

export type RebuildRevisionQuery = {
  action?: 'dependencies';
};

export type RebuildRevisionVariables = {
  revisionLocator: string;
  query?: RebuildRevisionQuery;
};

//  Note that the response won't have actual Error instances,
//  because Express will serialize the error.
export type RebuildRevisionResponse = Array<Build | Error>;

//  Function for rebuilding a revision.
async function rebuildRevision({ revisionLocator, query }: RebuildRevisionVariables) {
  const response = await get<RebuildRevisionResponse>(`/api/revisions/${nuc(revisionLocator)}/rebuild`, false, query);
  return response.data as RebuildRevisionResponse;
}

/**
 * Shared helper to create the info toast when a rebuild starts.
 * Used by both useRebuildRevision and useRebuildUserRevision.
 */
function createRebuildToast(variables: RebuildRevisionVariables, onAddToast: (toast: NewToast) => void): void {
  const revision = Locator.parse(variables.revisionLocator).revision ?? 'revision';
  const displayRevision = revision.length > 10 ? `${revision.slice(0, 10)}...` : revision;
  onAddToast({
    type: 'info',
    content: `Analyzing "${displayRevision}".`,
  });
}

/**
 * Base mutation for rebuilding a revision (dependency).
 *
 * Features:
 * - Shows an info toast when build starts
 * - Returns `isPending` state
 *
 * Note: For user-facing UI that needs full toast notifications and query invalidation,
 * use `useRebuildUserRevision` instead.
 *
 * @example
 * const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildRevision();
 *
 * await rebuild({
 *   revisionLocator: 'custom+1/my-project$1.0.0'
 * });
 */
export default function useRebuildRevision() {
  const onAddToast = useAddToast();

  return useMutation<RebuildRevisionVariables, RebuildRevisionResponse>({
    mutationFn: rebuildRevision,
    onMutate: (variables) => createRebuildToast(variables, onAddToast),
  });
}

/**
 * Mutation for rebuilding a user project revision with full toast notifications.
 *
 * Features:
 * - Shows an info toast when build starts
 * - Automatically adds the build job to the notification queue
 * - Returns `isPending` state (to replace Reflux state Revision.status.submitting_build)
 * - Invalidates revision queries on success
 *
 * This is the hook you should use in user-facing UI components. For simpler
 * use cases that don't need the notification queue or query invalidation,
 * use `useRebuildRevision` instead.
 *
 * @example
 * const { mutateAsync: rebuild, isPending: isRebuilding } = useRebuildUserRevision();
 *
 * await rebuild({
 *   revisionLocator: 'custom+1/my-project$1.0.0'
 * });
 */
export function useRebuildUserRevision() {
  const onAddToast = useAddToast();
  const addJob = useAddJobToNotificationQueue();

  return useMutation<RebuildRevisionVariables, RebuildRevisionResponse>({
    mutationFn: rebuildRevision,
    onMutate: (variables) => createRebuildToast(variables, onAddToast),
    onSuccess: (buildsOrErrors: RebuildRevisionResponse, variables: RebuildRevisionVariables) => {
      if (buildsOrErrors) {
        buildsOrErrors.forEach((buildOrError) => {
          if (_.isError(buildOrError)) {
            return;
          }
          // Queue a job notification for the build.
          const { task, jobToken } = buildOrError.task ?? {};
          if (jobToken && task) {
            addJob(jobToken, task);
          }
        });
      }

      //  Invalidate the revision data for this locator.
      queryClient.invalidateQueries({ queryKey: ['revision', variables.revisionLocator] });
    },
  });
}
